/**
 * Bel, Mail en WhatsApp bij een klant (sessie 46, B1 en B2; sessie 72).
 *
 * - **Bel** zonder open zaak (klantpagina) opent het belvenster los
 *   (sessie 47, 9d): de klik maakt geen zaak; het gesprek dat Rinkel
 *   meldt, krijgt pas een telefoonzaak. Met een open zaak opent Vandaag die
 *   zaak met het vak op Bellen.
 * - **Mail** en **WhatsApp** (sessie 72, punt 10) zetten een nieuwe zaak
 *   klaar zoals een inkomende (`POST /api/werkplek/zaken/nieuw`) en openen
 *   hem op Vandaag met het antwoordvak in dat kanaal
 *   (`/?zaak=<id>&vak=mail|whatsapp`). Is de open zaak al van dat
 *   kanaal, dan is het die zaak. Er wordt nooit iets verstuurd: de tekst
 *   schrijf je in het vak.
 */
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { vertaalApiFout } from "@/lib/api";
import { kaartenApi } from "@/lib/werkplek-api";

import { openLosBellen } from "./losBellen";

export type KlantKanaal = "mail" | "whatsapp" | "telefoon";

export interface KlantActie {
  kanaal: KlantKanaal;
  klantId: string;
  /** De zaak die nu open staat, als die er is. */
  zaakId?: string | null;
  /** Het kanaal van die zaak (`kanaal_hoofd`). */
  zaakKanaal?: string | null;
  email?: string | null;
  nummer?: string | null;
}

/** Is de open zaak al van dit kanaal? `kanaal_hoofd` zegt "email". */
function zelfdeKanaal(actie: KlantActie): boolean {
  if (!actie.zaakId) return false;
  if (actie.kanaal === "telefoon") return true;
  const zaak = actie.zaakKanaal === "email" ? "mail" : actie.zaakKanaal;
  return zaak === actie.kanaal;
}

export function useKlantActies() {
  const navigeer = useNavigate();
  const start = useMutation({
    mutationFn: (actie: KlantActie & { kanaal: "mail" | "whatsapp" }) =>
      kaartenApi.nieuwBericht({
        klant_id: actie.klantId,
        kanaal: actie.kanaal,
        stem: actie.kanaal === "whatsapp" ? "nova" : "sanne",
        bron: actie.zaakId ? "klantblok" : "klantpagina",
      }),
  });

  async function voerUit(actie: KlantActie): Promise<void> {
    if (zelfdeKanaal(actie) && actie.zaakId) {
      navigeer(`/?zaak=${actie.zaakId}&vak=${actie.kanaal}`);
      return;
    }
    // Sessie 47 (9d): bellen zonder open zaak maakt geen zaak. Het
    // belvenster opent los; de telefoonzaak ontstaat pas als Rinkel het
    // gesprek meldt.
    if (actie.kanaal === "telefoon") {
      openLosBellen({
        klantId: actie.klantId,
        nummer: actie.nummer ?? null,
        bron: "klantpagina",
      });
      return;
    }
    const uitkomst = await start.mutateAsync({ ...actie, kanaal: actie.kanaal });
    if (!uitkomst.zaak_id) return;
    navigeer(`/?zaak=${uitkomst.zaak_id}&vak=${actie.kanaal}`);
  }

  return {
    voerUit,
    bezig: start.isPending,
    fout: start.isError ? vertaalApiFout(start.error) : null,
  };
}
